/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiButton, EuiFlexGroup } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { addToast } from '@modules/cui/Toasts'
import DeleteProjectButton from '@modules/project-components/ProjectPage/DeleteProjectButton'
import type { AnyUserProject } from '@modules/project-user-api/types'
import type { MultiErrorResponse } from '@modules/project-user-api/v1/types'
import { getDeleteProjectMutationHook } from '@modules/project-user-lib/hooks/delete'
import { projectsUrl } from '@modules/project-user-lib/urls'
import { useDeleteProjectErrorMutation } from '@modules/project-user-pages/hooks'
import history from '@modules/utils/history'

export type Props = {
  project: AnyUserProject
  onComplete: () => void
}

const UserDeleteProjectButton: React.FunctionComponent<Props> = ({ project, onComplete }) => {
  const { mutate: deleteErrorDetails } = useDeleteProjectErrorMutation()

  const deleteProjectMutation = getDeleteProjectMutationHook(project.type)({
    onMutate: () => {
      history.push(projectsUrl())
    },

    onError: (error) => {
      const deletionErrorTitle = (
        <FormattedMessage
          id='toasts.title-deleting-project-failed'
          defaultMessage='Deleting your project failed'
        />
      )

      if ('errors' in (error as MultiErrorResponse)) {
        const errors = error as MultiErrorResponse
        errors.errors.forEach((errorSubItem) => {
          addToast({
            family: 'toast-error',
            color: 'danger',
            iconType: 'broom',
            title: deletionErrorTitle,
            text: (
              <EuiFlexGroup direction='column'>
                <FormattedMessage
                  id='toasts.text-deleting-project-failed'
                  defaultMessage='Please try again later'
                />
                <FormattedMessage
                  id='toasts.text-deleting-project-failed-more'
                  defaultMessage='<linkButton>Show details about the error.</linkButton>'
                  values={{
                    linkButton: (chunks: string) => (
                      <EuiButton
                        onClick={() => {
                          deleteErrorDetails(errorSubItem)
                        }}
                      >
                        {chunks}
                      </EuiButton>
                    ),
                  }}
                />
              </EuiFlexGroup>
            ),
          })
        })

        return
      }

      addToast({
        family: 'toast-error',
        color: 'danger',
        iconType: 'broom',
        title: deletionErrorTitle,
        text: (
          <FormattedMessage
            id='toasts.text-deleting-project-failed'
            defaultMessage='Please try again later'
          />
        ),
      })
    },
  })

  return (
    <DeleteProjectButton
      project={project}
      onDelete={performDelete}
      onComplete={onComplete}
      isLoading={deleteProjectMutation.isLoading}
    />
  )

  function performDelete() {
    deleteProjectMutation.mutate({ id: project.id })
  }
}

export default UserDeleteProjectButton
